import type { Address, IdentityFormData, IdentityRole, KycEmployedActivityRow, KycSelfEmployedActivityRow } from "@/screen/Identities/Interfaces/IdentityFormInterfaces";
import type { AddressDto, UserIdentityDetailDto, SaveUserIdentityDto, SaveUserIdentityAddressDto, SaveKycEmployedActivityDto, SaveKycSelfEmployedActivityDto, ChangedDocumentsDto } from "@/services/UserIdentitiesService";
import { parseIsoDate, formatDateOnly } from "@/utils/DateFormatter";

function mapAddressDto(dto: AddressDto | null | undefined): Address | null
{
    if(!dto)
    {
        return null;
    }
    return{
        roadType: dto.roadType ?? "",
        streetName: dto.streetName ?? "",
        streetNumber: dto.streetNumber ?? "",
        streetFloor: dto.streetFloor ?? "",
        streetStair: dto.streetStair ?? "",
        streetDoor: dto.streetDoor ?? "",
        streetOther: dto.streetOther ?? "",
        zipCode: dto.zipCode ?? "",
        city: dto.city ?? "",
        state: dto.state ?? "",
        // El país se cura con `||`, no con `??`: los registros heredados llegan con cadena vacía
        // (AddressDto.country no es nullable), y `??` no la cubre. El campo no tiene input propio
        // en el formulario pero sí lo exige isAddressValid, así que una cadena vacía dejaba la
        // dirección inválida sin ningún campo que el usuario pudiera corregir. Mismo criterio que
        // los espejos de sameAddress en el controller.
        country: dto.country || "ES"
    };
}

function addressesAreEquivalent(a: Address | null, b: Address | null): boolean
{
    if(!a && !b)
    {
        return true;
    }
    if(!a || !b)
    {
        return false;
    }
    const keys: (keyof Address)[] =
    [
        "roadType", "streetName", "streetNumber", "streetFloor", "streetStair", "streetDoor", "streetOther",
        "zipCode", "city", "state", "country"
    ];
    return keys.every((k) => (a[k] ?? "") === (b[k] ?? ""));
}

function inferUseSinglePhone(dto: UserIdentityDetailDto): boolean
{
    const phone = dto.phone ?? "";
    const countryPrefix = dto.countryPrefix ?? "";
    const op = dto.operationPhone ?? "";
    const ocp = dto.operationCountryPrefix ?? "";
    const sp = dto.signPhone ?? "";
    const scp = dto.signCountryPrefix ?? "";
    return phone !== "" && phone === op && phone === sp && countryPrefix === ocp && countryPrefix === scp;
}

function parseIdType(value: string | null | undefined): "DNI" | "NIE" | ""
{
    const normalized = value ? value.toUpperCase() : "";
    if(normalized === "NIE")
    {
        return "NIE";
    }
    if(normalized === "DNI")
    {
        return "DNI";
    }
    return "";
}

function parseProfessionFromDto(value: string | number | null | undefined): string | null
{
    if(value === null || value === undefined)
    {
        return null;
    }
    const s = String(value).trim();
    return s === "" ? null : s;
}

function parseOptionalBooleanFromApi(value: string | boolean | number | null | undefined): boolean | undefined
{
    if(value === null || value === undefined || value === "")
    {
        return undefined;
    }
    if(typeof value === "boolean")
    {
        return value;
    }
    if(typeof value === "number")
    {
        if(value === 1)
        {
            return true;
        }
        if(value === 0)
        {
            return false;
        }
        return undefined;
    }
    const v = value.trim().toLowerCase();
    if(v === "true" || v === "1" || v === "sí" || v === "si" || v === "yes")
    {
        return true;
    }
    if(v === "false" || v === "0" || v === "no")
    {
        return false;
    }
    return undefined;
}

function mapAddressToDto(address: Address | null | undefined): SaveUserIdentityAddressDto | undefined
{
    if(!address)
    {
        return undefined;
    }
    return{
        roadType: address.roadType,
        streetName: address.streetName,
        streetNumber: address.streetNumber,
        streetFloor: address.streetFloor,
        streetStair: address.streetStair,
        streetDoor: address.streetDoor,
        streetOther: address.streetOther,
        zipCode: address.zipCode,
        city: address.city,
        state: address.state,
        country: address.country,
    };
}

function parseEconomicActivityFromDto(value: string | number | null | undefined): string | null
{
    if(value === null || value === undefined)
    {
        return null;
    }
    const mapped = String(value).trim();
    if(mapped === "SELF_EMPLOYED" || mapped === "OTHER_EMPLOYEES")
    {
        return mapped;
    }
    return null;
}

function mapEconomicActivityForSave(value: string | null | undefined): "SELF_EMPLOYED" | "OTHER_EMPLOYEES" | undefined
{
    if(value === "SELF_EMPLOYED" || value === "OTHER_EMPLOYEES")
    {
        return value;
    }

    return undefined;
}

function parseCodeFromDto(value: string | null | undefined): string | null
{
    if(value === null || value === undefined)
    {
        return null;
    }
    const code = value.trim();
    return code === "" ? null : code;
}

function mapEmployedActivityRowsFromDto(rows: SaveKycEmployedActivityDto[] | null | undefined): KycEmployedActivityRow[]
{
    if(!Array.isArray(rows))
    {
        return [];
    }
    return rows.map((row) =>
    ({
        activityStatus: parseCodeFromDto(row.activityStatus),
        cnoCode: parseCodeFromDto(row.cnoCode),
        years: row.years ?? null,
        employedCurrentCompany: parseCodeFromDto(row.employedCurrentCompany),
    }));
}

function mapSelfEmployedActivityRowsFromDto(rows: SaveKycSelfEmployedActivityDto[] | null | undefined): KycSelfEmployedActivityRow[]
{
    if(!Array.isArray(rows))
    {
        return [];
    }
    return rows.map((row) =>
    ({
        activityStatus: parseCodeFromDto(row.activityStatus),
        cnaeCode: parseCodeFromDto(row.cnaeCode),
        years: row.years ?? null,
        relatedProfessionalActivity: parseCodeFromDto(row.relatedProfessionalActivity),
    }));
}

/**
 * Colecciones de actividades para el guardado (semántica de REEMPLAZO del backend):
 * puerta true = enviar las filas tal cual; puerta false = enviar [] (borra todas);
 * puerta sin contestar = undefined (no tocar las filas persistidas).
 */
function mapEmployedActivitiesForSave(gate: boolean | undefined, rows: KycEmployedActivityRow[]): SaveKycEmployedActivityDto[] | undefined
{
    if(gate === undefined)
    {
        return undefined;
    }
    if(gate === false)
    {
        return [];
    }
    return rows.map((row) =>
    ({
        activityStatus: row.activityStatus ?? undefined,
        cnoCode: row.cnoCode ?? undefined,
        years: row.years ?? undefined,
        employedCurrentCompany: row.employedCurrentCompany ?? undefined,
    }));
}

function mapSelfEmployedActivitiesForSave(gate: boolean | undefined, rows: KycSelfEmployedActivityRow[]): SaveKycSelfEmployedActivityDto[] | undefined
{
    if(gate === undefined)
    {
        return undefined;
    }
    if(gate === false)
    {
        return [];
    }
    return rows.map((row) =>
    ({
        activityStatus: row.activityStatus ?? undefined,
        cnaeCode: row.cnaeCode ?? undefined,
        years: row.years ?? undefined,
        relatedProfessionalActivity: row.relatedProfessionalActivity ?? undefined,
    }));
}

export function buildChangedDocumentsDto(data: IdentityFormData): ChangedDocumentsDto
{
    return{
        dni: data.idFront instanceof File || data.idBack instanceof File,
        proofOfIncome: data.proofOfIncome instanceof File,
        proofOfIncomeMinor: data.proofOfMinorGuardian instanceof File,
    };
}

export function mapFormDataToSaveDto(data: IdentityFormData): SaveUserIdentityDto
{
    const economicActivity = mapEconomicActivityForSave(data.economicActivity);

    return{
        id: data.id ?? 0,
        // Se envían tal cual: el backend limpia (PersonNameRules) solo lo que cambia. Limpiarlos aquí haría que un
        // nombre legacy sin tocar llegase "cambiado" y bloquease la edición de un interviniente formalizado.
        firstName: data.firstName || undefined,
        lastName: data.lastName || undefined,
        secondLastName: data.secondLastName || undefined,
        dni: data.idDocument || undefined,
        birthday: formatDateOnly(data.birthday),
        // Con validez permanente no se envía fecha: la resuelve el backend a partir del flag.
        idExpiration: data.hasPermanentIdExpiration ? null : formatDateOnly(data.idExpiration),
        hasPermanentIdExpiration: data.hasPermanentIdExpiration,
        birthdayCountry: data.birthdayCountry || undefined,
        nationalCountry: data.nationalCountry || undefined,
        idType: data.idType || undefined,
        proofOfMinorGuardianDescription: data.proofOfMinorGuardianDescription || undefined,
        gafiQuestion: data.gafiQuestion,
        publicPosition: data.publicPosition,
        email: data.email || undefined,
        phone: data.phone || undefined,
        countryPrefix: data.countryPrefix || undefined,
        operationPhone: data.useSinglePhone ? undefined : (data.operationPhone || undefined),
        operationCountryPrefix: data.useSinglePhone ? undefined : (data.operationCountryPrefix || undefined),
        signPhone: data.useSinglePhone ? undefined : (data.signaturePhone || undefined),
        signCountryPrefix: data.useSinglePhone ? undefined : (data.signatureCountryPrefix || undefined),
        useSinglePhone: data.useSinglePhone,
        postalAddress: mapAddressToDto(data.postalAddress),
        fiscalAddress: data.sameAddress ? undefined : mapAddressToDto(data.fiscalAddress),
        // El flag debe viajar explícito: sin él, el backend conserva el SameAddress persistido y la
        // formalización falla (SAME_ADDRESS_REQUIRED) cuando quedaba en NULL. true = misma dirección
        // (el backend limpia la fiscal); false = distintas (se envía la fiscal arriba).
        sameAddress: data.sameAddress,
        main: data.main,
        maritalStatus: data.maritalStatus ?? undefined,
        residenceCountry: data.residenceCountry ?? undefined,
        fatcaStatus: data.fatcaStatus ?? undefined,
        hasFiscalNumberEEUU: data.hasTaxIdInUS,
        hasResidenceEEUU: data.hasResidenceInUS,
        hasTaxesEEUU: data.hasTaxObligationInUS,
        residenceOfficialDocument: data.residenceOfficialDocument ?? undefined,
        noResidenceDocument: data.noResidenceDocument ?? undefined,
        profession: data.profession ?? undefined,
        economicActivity,
        annualGrossIncome: data.annualGrossIncome ?? undefined,
        hasEmployedActivity: data.hasEmployedActivity,
        hasSelfEmployedActivity: data.hasSelfEmployedActivity,
        employedActivities: mapEmployedActivitiesForSave(data.hasEmployedActivity, data.employedActivities),
        selfEmployedActivities: mapSelfEmployedActivitiesForSave(data.hasSelfEmployedActivity, data.selfEmployedActivities),
        fundsOrigin: data.fundsOrigin ?? undefined,
        fundsDocumentationProvided: data.fundsDocumentationProvided ?? undefined,
        internationalCommercialActivity: data.internationalCommercialActivity,
        isPepRelative: data.isPepRelative,
        numberOfDependants: data.numberOfDependants ?? undefined,
        otherNationalities: data.otherNationalities,
        otherNationality: data.otherNationality.trim() === "" ? undefined : data.otherNationality.trim(),
        otherPersonalCircumstances: data.otherPersonalCircumstances ?? undefined,
        riskCountries: data.riskCountries,
        riskJurisdictionActivity: data.riskJurisdictionActivity,
        maritalSituation: data.maritalSituation ?? undefined,
        isUsCitizen: data.isUsCitizen,
    };
}

// El adjunto viaja como fichero aparte o como la URL ya persistida; una cadena vacía no es ninguna
// de las dos cosas y el backend la tomaría por un borrado del documento.
function storedAttachmentValue(value: unknown): string | undefined
{
    return typeof value === "string" && value.trim() !== "" ? value : undefined;
}

export function buildSaveUserIdentityDraftFormData(data: IdentityFormData): FormData
{
    const dto = mapFormDataToSaveDto(data);
    const changedDocuments = buildChangedDocumentsDto(data);

    const jsonBody: Record<string, unknown> =
    {
        ...dto,
        idFront: data.idFront instanceof File ? undefined : storedAttachmentValue(data.idFront),
        idBack: data.idBack instanceof File ? undefined : storedAttachmentValue(data.idBack),
        proofOfIncome: data.proofOfIncome instanceof File ? undefined : storedAttachmentValue(data.proofOfIncome),
        changedDocuments,
    };

    const fd = new FormData();
    fd.append("data", JSON.stringify(jsonBody));

    if(data.idFront instanceof File)
    {
        fd.append("idFront", data.idFront);
    }
    if(data.idBack instanceof File)
    {
        fd.append("idBack", data.idBack);
    }
    if(data.proofOfIncome instanceof File)
    {
        fd.append("proofOfIncome", data.proofOfIncome);
    }
    if(data.proofOfMinorGuardian instanceof File)
    {
        fd.append("proofOfMinorGuardian", data.proofOfMinorGuardian);
    }

    return fd;
}

export function mapUserIdentityDetailDtoToFormData(dto: UserIdentityDetailDto): IdentityFormData
{
    const postalAddress = mapAddressDto(dto.postalAddress);
    const fiscalAddress = mapAddressDto(dto.fiscalAddress);
    const sameAddress = fiscalAddress === null && postalAddress !== null ? true : addressesAreEquivalent(postalAddress, fiscalAddress);

    const role: IdentityRole = "Owner";
    // La fecha centinela de la validez permanente no se muestra: el backend informa el flag y el
    // campo de fecha queda vacío y deshabilitado.
    const hasPermanentIdExpiration = dto.hasPermanentIdExpiration === true;

    return{
        id: dto.id,
        firstName: dto.firstName ?? "",
        lastName: dto.lastName ?? "",
        secondLastName: dto.secondLastName ?? "",
        email: dto.email ?? "",
        countryPrefix: dto.countryPrefix ?? "",
        phone: dto.phone ?? "",
        operationCountryPrefix: dto.operationCountryPrefix ?? "",
        operationPhone: dto.operationPhone ?? "",
        signatureCountryPrefix: dto.signCountryPrefix ?? "",
        signaturePhone: dto.signPhone ?? "",
        useSinglePhone: inferUseSinglePhone(dto),
        birthday: parseIsoDate(dto.birthday),
        birthdayCountry: dto.birthdayCountry ?? "ES",
        nationalCountry: dto.nationalCountry ?? "ES",
        idType: parseIdType(dto.idType),
        idDocument: dto.dni ?? "",
        idExpiration: hasPermanentIdExpiration ? null : parseIsoDate(dto.idExpiration),
        hasPermanentIdExpiration,
        idFront: dto.idFront ?? null,
        idBack: dto.idBack ?? null,
        postalAddress,
        fiscalAddress: sameAddress ? null : fiscalAddress,
        sameAddress,
        role,
        main: dto.main,
        isMinor: dto.isMinor === true,
        economicActivity: parseEconomicActivityFromDto(dto.economicActivity),
        proofOfIncome: dto.proofOfIncome ?? null,
        gafiQuestion: parseOptionalBooleanFromApi(dto.gafiQuestion ?? undefined),
        publicPosition: parseOptionalBooleanFromApi(dto.publicPosition ?? undefined),
        maxContribution: null,
        // La URL persistida alimenta la previsualización igual que idFront/idBack; solo un File cuenta
        // como documento nuevo (buildChangedDocumentsDto), así que no se reenvía al guardar.
        proofOfMinorGuardian: dto.proofOfMinorGuardianDescription || null,
        proofOfMinorGuardianDescription: dto.proofOfMinorGuardianDescription ?? "",
        maritalStatus: dto.maritalStatus ?? null,
        profession: parseProfessionFromDto(dto.profession),
        residenceCountry: dto.residenceCountry ?? null,
        fatcaStatus: dto.fatcaStatus ?? null,
        residenceOfficialDocument: dto.residenceOfficialDocument ?? null,
        noResidenceDocument: dto.noResidenceDocument ?? null,
        hasTaxIdInUS: dto.hasFiscalNumberEEUU ?? undefined,
        hasResidenceInUS: dto.hasResidenceEEUU ?? undefined,
        hasTaxObligationInUS: dto.hasTaxesEEUU ?? undefined,
        annualGrossIncome: parseCodeFromDto(dto.annualGrossIncome),
        hasEmployedActivity: parseOptionalBooleanFromApi(dto.hasEmployedActivity ?? undefined),
        hasSelfEmployedActivity: parseOptionalBooleanFromApi(dto.hasSelfEmployedActivity ?? undefined),
        employedActivities: mapEmployedActivityRowsFromDto(dto.employedActivities),
        selfEmployedActivities: mapSelfEmployedActivityRowsFromDto(dto.selfEmployedActivities),
        fundsOrigin: parseCodeFromDto(dto.fundsOrigin),
        fundsDocumentationProvided: parseCodeFromDto(dto.fundsDocumentationProvided),
        internationalCommercialActivity: parseOptionalBooleanFromApi(dto.internationalCommercialActivity ?? undefined),
        isPepRelative: parseOptionalBooleanFromApi(dto.isPepRelative ?? undefined),
        numberOfDependants: dto.numberOfDependants ?? null,
        otherNationalities: parseOptionalBooleanFromApi(dto.otherNationalities ?? undefined),
        otherNationality: dto.otherNationality ?? "",
        otherPersonalCircumstances: parseCodeFromDto(dto.otherPersonalCircumstances),
        riskCountries: parseOptionalBooleanFromApi(dto.riskCountries ?? undefined),
        riskJurisdictionActivity: parseOptionalBooleanFromApi(dto.riskJurisdictionActivity ?? undefined),
        maritalSituation: parseCodeFromDto(dto.maritalSituation),
        isUsCitizen: parseOptionalBooleanFromApi(dto.isUsCitizen ?? undefined)
    };
}